<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { computed, onMounted, ref } from 'vue'
  import {
    type CustomerOrganisation as Organisation,
    type CustomerUser as User,
    PlaceholderKeyEnum,
    OrganisationsService,
    type Placeholder,
    UsersService
  } from '@/types/generated'
  import { usePlaceholderStore } from '@/shared/stores/placeholderStore'

  const { fetchPlaceholders } = usePlaceholderStore()
  const placeholders = computed<Placeholder>(() => usePlaceholderStore().placeholders)
  const fullSignaturePlaceholder = computed<Placeholder>(() => {
    return placeholders.value?.find(
      (placeholder) => placeholder.key === PlaceholderKeyEnum.FULL_SIGNATURE
    )
  })
  const personalSignaturePlaceholder = computed<Placeholder>(() => {
    return placeholders.value?.find(
      (placeholder) => placeholder.key === PlaceholderKeyEnum.CLERK_SIGNATURE
    )
  })
  const lawFirmSignaturePlaceholder = computed<Placeholder>(() => {
    return placeholders.value?.find(
      (placeholder) => placeholder.key === PlaceholderKeyEnum.LAWFIRM_SIGNATURE
    )
  })

  const org = ref<Organisation>()
  const user = ref<User>()

  const organisationSignature = computed({
    get: () => org.value?.email_signature || '',
    set: async (value: string) => {
      if (org.value) {
        org.value.email_signature = value
        await OrganisationsService.organisationsPartialUpdate({
          uuid: org.value.uuid,
          requestBody: { email_signature: value }
        })
      }
    }
  })

  const userSignature = computed({
    get: () => user.value?.email_signature || '',
    set: async (value: string) => {
      if (user.value) {
        user.value.email_signature = value
        await UsersService.usersPartialUpdate({
          uuid: user.value.uuid,
          requestBody: { email_signature: value }
        })
      }
    }
  })

  onMounted(async () => {
    const fetching = [fetchUser(), fetchOrganisation()]
    if (!placeholders.value) {
      fetching.push(fetchPlaceholders())
    }
    await Promise.all(fetching)
  })

  const fetchUser = async () => {
    user.value = (await UsersService.usersRetrieve({
      uuid: 'current'
    })) as User
  }

  const fetchOrganisation = async () => {
    org.value = (await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })) as Organisation
  }
</script>

<template>
  <SettingsPage v-if="user && org && placeholders">
    <div class="mb-3 block font-medium">Signatur</div>
    <p class="mb-4 me-20 text-sm text-gray-600">
      E-Mails, die Sie über JUPUS versenden, bestehen aus Ihrer persönlichen Signatur und der
      Signatur Ihrer Kanzlei. Der Platzhalter
      <span
        v-if="fullSignaturePlaceholder"
        v-html="fullSignaturePlaceholder.html"
      ></span>
      fügt beide Signaturen automatisch in Ihre E-Mail ein und sollte daher in jeder E-Mail-Vorlage
      verwendet werden.
    </p>

    <div class="mb-3 block font-medium">Persönliche Signatur</div>
    <p class="mb-4 me-20 text-sm text-gray-600">
      Legen Sie hier Ihre persönliche Signatur individuell fest. Alle E-Mails, die Sie über JUPUS
      als zugewiesener Sachbearbeiter versenden, enthalten diese Signatur.
    </p>
    <div class="mb-6">
      <RichTextEditor
        v-model="userSignature"
        :font-color="org.email_text_color"
        :font-family="org.email_text_font"
        :font-size="org.email_text_size"
        :private-file-upload="false"
        :enable-text-styling-buttons="true"
        :enable-code-view-button="true"
      />
    </div>

    <div class="mb-3 block font-medium">Kanzlei-Signatur</div>
    <p class="mb-4 me-20 text-sm text-gray-600">
      Legen Sie hier die Signatur der Kanzlei fest. Diese Signatur dient vor Allem für
      Standardformulierungen, die in jeder E-Mail enthalten sein sollen.
    </p>
    <div class="mb-6">
      <RichTextEditor
        v-model="organisationSignature"
        :font-color="org.email_text_color"
        :font-family="org.email_text_font"
        :font-size="org.email_text_size"
        :private-file-upload="false"
        :enable-text-styling-buttons="true"
        :enable-code-view-button="true"
      />
    </div>
  </SettingsPage>
</template>
